import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Sparkles, Loader2, AlertTriangle, CheckCircle2, Wand2, ArrowRight } from "lucide-react";
import { chPrepararDocumento, chAnalizarInfo } from "@/lib/prevelis";
import { Campo, visible } from "@/pages/SuizaFields";

export default function SuizaVerificacionPrevia({ empresaId, tipo, idioma, onClose, onGenerate }) {
  const fr = idioma === "fr";
  const [data, setData] = useState(null);
  const [ans, setAns] = useState({});
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const L = (o) => (fr ? o.fr : o.es) || o.es;

  useEffect(() => {
    chPrepararDocumento(empresaId, tipo, idioma)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); toast.error(fr ? "Préparation impossible" : "No se pudo preparar"); });
  }, [empresaId, tipo, idioma, fr]);

  const reqPend = data ? (data.extra_schema || []).some((sec) =>
    sec.fields.filter((f) => f.req && visible(f, ans)).some((f) => {
      const v = ans[f.k];
      return Array.isArray(v) ? v.length === 0 : !(v && String(v).trim());
    })
  ) : false;

  const altDoc =
    tipo === "investigacion_accidente" && ans.existe_accidente === "no"
      ? { codigo: "procedimiento_investigacion", es: "Generar Procedimiento de investigación de accidentes", fr: "Générer la Procédure d'enquête d'accidents et d'incidents" }
      : tipo === "inspeccion_seguridad" && ans.inspeccion_realizada === "no"
        ? { codigo: "checklist_inspeccion", es: "Generar Checklist de inspección", fr: "Générer la Liste de contrôle d'inspection" }
        : null;

  const bloqueado = (data && data.faltantes.obligatorio.length > 0) || reqPend || !!altDoc;
  const generar = async (tipoOverride) => { setGen(true); try { await onGenerate(ans, tipoOverride); } finally { setGen(false); } };

  const analizar = async () => {
    setAnalizando(true);
    try { setAnalisis(await chAnalizarInfo(empresaId, tipo, idioma)); }
    catch { toast.error(fr ? "Analyse impossible" : "No se pudo analizar"); }
    finally { setAnalizando(false); }
  };

  const Lista = ({ titulo, items, color }) => (items && items.length) ? (
    <div className="mt-2">
      <p className={`text-xs font-semibold ${color}`}>{titulo}</p>
      <ul className="text-sm text-slate-600 list-disc pl-5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-3 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()} data-testid="ch-verif-previa">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg">{fr ? "Vérification préalable" : "Verificación previa"}</h3>
            <p className="text-xs text-slate-500">{data?.tipo_label} · {fr ? "confirmez avant de générer" : "confirma antes de generar"}</p>
          </div>
          <div className="flex items-center gap-2">
            {data && typeof data.completitud === "number" && (
              <span data-testid="ch-verif-completitud" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                ~{data.completitud}% {fr ? "complet (estimé)" : "completo (estimado)"}
              </span>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : !data ? (
          <p className="text-sm text-slate-500 py-6 text-center">—</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-800">{fr ? "Pré-analyse IA de l'information" : "Pre-análisis IA de la información"}</p>
                <button onClick={analizar} disabled={analizando} data-testid="ch-verif-analizar"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-indigo-500 text-indigo-600 text-sm font-medium hover:bg-indigo-50 disabled:opacity-50">
                  {analizando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {fr ? "Analyser l'information" : "Analizar información"}
                </button>
              </div>
              {analisis && (
                <div data-testid="ch-verif-analisis" className="mt-1">
                  <Lista titulo={fr ? "Disponible" : "Disponible"} items={analisis.disponible} color="text-emerald-700" />
                  <Lista titulo={fr ? "Manque essentiel" : "Falta esencial"} items={analisis.faltante_esencial} color="text-red-700" />
                  <Lista titulo={fr ? "Manque important" : "Falta importante"} items={analisis.faltante_importante} color="text-amber-700" />
                  <Lista titulo={fr ? "Vérification technique" : "Verificación técnica"} items={analisis.verificacion_tecnica} color="text-slate-700" />
                  <Lista titulo={fr ? "Questions suggérées" : "Preguntas sugeridas"} items={analisis.preguntas_sugeridas} color="text-indigo-700" />
                </div>
              )}
            </div>

            {data.faltantes.obligatorio.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="ch-verif-obligatorio">
                <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {fr ? "Données obligatoires manquantes" : "Faltan datos obligatorios"}</p>
                <ul className="mt-1 text-sm text-red-700 list-disc pl-5">{data.faltantes.obligatorio.map((x, i) => <li key={i}>{x}</li>)}</ul>
                <p className="text-xs text-red-600 mt-1">{fr ? "Complétez le questionnaire technique avant de générer." : "Complete el cuestionario técnico antes de generar."}</p>
              </div>
            )}
            {data.faltantes.recomendado.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="ch-verif-recomendado">
                <p className="text-sm font-semibold text-amber-700 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {fr ? "Recommandé (le document restera limité)" : "Recomendado (el documento quedará limitado)"}</p>
                <ul className="mt-1 text-sm text-amber-700 list-disc pl-5">{data.faltantes.recomendado.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
            {data.faltantes.obligatorio.length === 0 && data.faltantes.recomendado.length === 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 flex items-center gap-1.5" data-testid="ch-verif-ok">
                <CheckCircle2 className="w-4 h-4" /> {fr ? "Information suffisante pour générer." : "Información suficiente para generar."}
              </div>
            )}

            {(data.extra_schema || []).map((sec) => (
              <div key={sec.id} className="border border-slate-200 rounded-lg p-3" data-testid={`ch-verif-sec-${sec.id}`}>
                <p className="font-medium text-slate-800 text-sm mb-2">{L(sec)}</p>
                <div className="space-y-3">
                  {sec.fields.filter((f) => visible(f, ans)).map((f) => (
                    <div key={f.k}>
                      <label className="text-xs font-medium text-slate-600">{L(f)}{f.req && <span className="text-red-500"> *</span>}</label>
                      <Campo field={f} value={ans[f.k]} onChange={(v) => setAns((s) => ({ ...s, [f.k]: v }))} idioma={idioma} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {altDoc && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="ch-verif-alt">
                <p className="text-sm text-blue-800">{fr
                  ? "Ce document ne s'applique pas dans ce cas. Vous pouvez générer à la place :"
                  : "Este documento no procede en este caso. Puedes generar en su lugar:"}</p>
                <button onClick={() => generar(altDoc.codigo)} disabled={gen} data-testid="ch-verif-alt-generar"
                  className="mt-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
                  {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {fr ? altDoc.fr : altDoc.es}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5 border-t border-slate-100 pt-3">
          {reqPend && !loading && (
            <span className="text-xs text-red-600 mr-auto" data-testid="ch-verif-req-pend">{fr ? "Répondez aux questions obligatoires (*) pour générer." : "Responde las preguntas obligatorias (*) para generar."}</span>
          )}
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">{fr ? "Annuler" : "Cancelar"}</button>
          <button onClick={() => generar()} disabled={loading || bloqueado || gen} data-testid="ch-verif-generar"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-50">
            {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {fr ? "Générer le document" : "Generar documento"}
          </button>
        </div>
      </div>
    </div>
  );
}
